"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  drawPassages,
  filterPassages,
  LENGTH_BANDS,
  type LengthBand,
  MAX_COUNT,
  MIN_COUNT,
  TYPING_WPM,
  typingSeconds,
  wordCount,
} from "@/lib/tool-kit/random-fun/paragraph-draw";
import {
  type Passage,
  TOPICS,
  type Topic,
} from "@/lib/tool-kit/random-fun/paragraph-passages";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_PARAGRAPH_GENERATOR_STRINGS = {
  howManyParagraphs: "How many paragraphs (1–5)",
  generateParagraphs: "Generate Paragraphs",
  copied: "Copied!",
  copyAll: "Copy All",
  yourRandomParagraphs: "Your random paragraphs",
  topicLabel: "Topic",
  topics: {
    any: "Any topic",
    nature: "Nature",
    stories: "Stories",
    ideas: "Ideas and essays",
    history: "History and speeches",
  },
  lengthLabel: "Length",
  lengths: {
    any: "Any length",
    short: "Short (up to 60 words)",
    medium: "Medium (61–120 words)",
    long: "Long (over 120 words)",
  },
  matches:
    "one:{count} passage matches these filters|other:{count} passages match these filters",
  fewer:
    "one:Only {count} passage matches, so that is the one you get.|other:Only {count} passages match, so you get all of them.",
  includeCredits: "Include the credit line when copying",
  emptyPrompt:
    "Choose a topic and a length, then press Generate Paragraphs. Each passage comes from a book or speech published in the United States before 1931.",
  stats:
    "{words} words · {characters} characters · about {seconds} s to type at {wpm} words per minute",
  credit: "{author}, {work} ({year})",
  creditExcerpt: "{author}, {work} ({year}), excerpt",
  sourceLabel: "Source:",
} as const;

export type RandomParagraphStrings = typeof RANDOM_PARAGRAPH_GENERATOR_STRINGS;

type TopicChoice = Topic | "any";
type LengthChoice = LengthBand | "any";

function creditLine(passage: Passage, strings: RandomParagraphStrings) {
  const template = passage.excerpt ? strings.creditExcerpt : strings.credit;
  return t(template, {
    author: passage.author,
    work: passage.work,
    year: passage.year,
  });
}

function copyText(
  passages: readonly Passage[],
  strings: RandomParagraphStrings,
  withCredits: boolean
): string {
  return passages
    .map((passage) =>
      withCredits
        ? `${passage.text}\n— ${creditLine(passage, strings)}. ${strings.sourceLabel} ${passage.source}`
        : passage.text
    )
    .join("\n\n");
}

function PassageCard({
  passage,
  strings,
}: {
  passage: Passage;
  strings: RandomParagraphStrings;
}) {
  const fmt = useToolNumber();
  return (
    <figure className="space-y-2 rounded-lg border border-border bg-card p-4">
      <blockquote className="text-foreground text-sm leading-relaxed">
        {passage.text}
      </blockquote>
      <figcaption className={HINT}>
        {creditLine(passage, strings)}. {strings.sourceLabel}{" "}
        <a
          className="text-primary underline-offset-4 hover:underline"
          href={passage.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          {passage.source}
        </a>
      </figcaption>
      <p className={HINT}>
        {t(strings.stats, {
          words: fmt(wordCount(passage.text)),
          characters: fmt(passage.text.length),
          seconds: fmt(typingSeconds(passage.text)),
          wpm: fmt(TYPING_WPM),
        })}
      </p>
    </figure>
  );
}

export default function RandomParagraphGeneratorWidget() {
  const strings = useToolUi(RANDOM_PARAGRAPH_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [topic, setTopic] = useState<TopicChoice>("any");
  const [length, setLength] = useState<LengthChoice>("any");
  const [count, setCount] = useState("2");
  const [withCredits, setWithCredits] = useState(true);
  const [passages, setPassages] = useState<Passage[]>([]);
  const [drawn, setDrawn] = useState(false);

  const matching = filterPassages(topic, length).length;
  const asked = Number(count);

  function handleGenerate() {
    setPassages(drawPassages(asked, topic, length));
    setDrawn(true);
  }

  const topicOptions = (["any", ...TOPICS] as const).map((value) => ({
    value,
    label: strings.topics[value],
  }));
  const lengthOptions = (["any", ...LENGTH_BANDS] as const).map((value) => ({
    value,
    label: strings.lengths[value],
  }));
  const countOptions = Array.from(
    { length: MAX_COUNT - MIN_COUNT + 1 },
    (_, index) => ({
      value: String(MIN_COUNT + index),
      label: fmt(MIN_COUNT + index),
    })
  );

  return (
    <section className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="rpg-topic"
          label={strings.topicLabel}
          onChange={setTopic}
          options={topicOptions}
          value={topic}
        />
        <SelectField
          id="rpg-length"
          label={strings.lengthLabel}
          onChange={setLength}
          options={lengthOptions}
          value={length}
        />
        <SelectField
          id="rpg-count"
          label={strings.howManyParagraphs}
          onChange={setCount}
          options={countOptions}
          value={count}
        />
      </div>
      <p className={HINT}>
        {tPlural(strings.matches, locale, matching, { count: fmt(matching) })}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={matching === 0}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateParagraphs}
        </button>
        <CopyButton
          copiedLabel={strings.copied}
          disabled={passages.length === 0}
          label={strings.copyAll}
          text={copyText(passages, strings, withCredits)}
        />
        <Checkbox
          checked={withCredits}
          label={strings.includeCredits}
          onChange={setWithCredits}
        />
      </div>
      <div aria-live="polite" className="space-y-4">
        {drawn ? null : <Notice>{strings.emptyPrompt}</Notice>}
        {passages.length > 0 ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <h2 className={WIDGET_H2}>{strings.yourRandomParagraphs}</h2>
            {passages.length < asked ? (
              <p className={HINT}>
                {tPlural(strings.fewer, locale, passages.length, {
                  count: fmt(passages.length),
                })}
              </p>
            ) : null}
            {passages.map((passage) => (
              <PassageCard
                key={passage.id}
                passage={passage}
                strings={strings}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
